/**
 * 약관 동의 증거 기록 (2026-09-16)
 *
 * 왜: 전자상거래법 제17조 제5항은 계약 체결·공급 사실의 증명책임을 사업자에게 두고,
 *     대법원 88다카29177은 "동의한 이상 약관을 자세히 읽지 않았다는 이유로 구속력을 벗어날 수 없다"고 한다.
 *     그런데 결제 직전 약관 모달 동의가 DB에 한 줄도 남지 않아(2026-09-16 실측) 코드 경로로만 간접 증명하던 상태였다.
 * 무엇: 동의 버튼을 누른 순간, 이용자가 실제로 본 모달 DOM의 텍스트를 그대로 서버(record_terms_consent RPC)에 보낸다.
 *      서버가 원문을 sha256으로 해시해 버전 표에 1회 저장하고, 동의 표에 회원·상품·해시·시각·IP·UA를 남긴다.
 * 원칙: 결제 흐름을 절대 막지 않는다 — 실패해도 조용히 경고만 남기고 끝난다(void 호출).
 */
import { supabase } from './supabase';

export type TermsKey =
    | 'visitor_analysis'   // 24시간 방문자 분석 약관 모달
    | 'alt_account'        // 가 계정 분석 약관 모달
    | 'visitor_reveal'     // 방문자 1인 공개 1단계(약관 + 환불불가 별도 체크)
    | 'post_viewer'        // 최근 게시물 조회자 1단계
    | 'close_circle'       // 관심 TOP 3 4단계
    | 'block_watch'        // 차단·언팔 72시간 분석 2단계(약관 + 청약철회 제한 별도 체크) — 2026-10-01
    | 'ceo_review'         // CEO 직접 검수 결제 모달(고지 + 체크박스)
    | 'vip_plan'           // VIP 플랜 약관 모달
    | 'signup_terms'       // 회원가입 이용약관 체크박스
    | 'referral_program';  // 리퍼럴 프로그램 무료 참여 약관 (2026-10-01, 서버 partner_status가 이 기록으로 참여 판정)

export interface RecordTermsConsentOptions {
    termsKey: TermsKey;
    /** 이용자가 본 약관이 들어 있는 DOM 요소(모달 루트). text가 없을 때 여기서 텍스트를 뽑는다. */
    element?: HTMLElement | null;
    /** DOM 대신 직접 넘기는 약관 텍스트 */
    text?: string;
    /** 결제 건 참조(분석 요청 id 등). 없으면 null */
    referenceId?: string | null;
    /** 대상 아이디·크레딧 수 등 부가 정보 */
    meta?: Record<string, unknown>;
    /** 약관 텍스트 안에 섞여 보이는 개인 값(대상 아이디 등). 해시 전에 자리표시자로 바꿔 버전이 사람마다 갈라지지 않게 하고 원문에 개인 값이 남지 않게 한다. 실제 값은 meta에 넣는다. */
    redact?: Array<string | null | undefined>;
}

// vite.config.ts define — Netlify 빌드의 COMMIT_REF. "어느 코드 버전이 이 약관을 보여줬나"를 기록에 묶는다.
declare const __APP_COMMIT__: string;
const APP_COMMIT: string = typeof __APP_COMMIT__ === 'string' ? __APP_COMMIT__ : '';

// 전화번호는 어느 화면에 섞여 있든 약관 원문에 남기지 않는다(2026-09-17 실측: TOP3·CEO 검수 시트에 결과 수신 번호가 렌더됨)
const PHONE_RE = /(\+?82[-\s.]?|0)1[016789][-\s.]?\d{3,4}[-\s.]?\d{4}/g;

function redactText(text: string, values: Array<string | null | undefined> | undefined): string {
    let out = text.replace(PHONE_RE, '{{phone}}');
    for (const v of values ?? []) {
        const val = (v ?? '').trim();
        if (val.length < 2) continue;
        out = out.split(val).join('{{redacted}}');
    }
    return out;
}

const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();

/**
 * 약관 텍스트 수집 — CSS(반응형 hidden)나 애니메이션 상태에 따라 값이 갈라지지 않도록
 * 렌더 결과(innerText)가 아니라 DOM 텍스트 노드를 직접 걷는다. 블록 사이는 공백으로 잇는다.
 * `data-consent-ignore`가 붙은 하위 요소(시연 애니메이션 등 약관이 아닌 것)와 script/style은 뺀다.
 * (2026-09-16 실측: 게시물 조회자 1단계의 폰 시연 애니메이션 글자가 섞여 같은 약관이 14개 버전으로 갈라졌고,
 *  방문자 분석 모달은 데스크톱에만 보이는 부제 한 줄 때문에 기기별로 2개 버전이 됐다.)
 */
export function extractTermsText(el: HTMLElement | null | undefined): string {
    if (!el) return '';
    const parts: string[] = [];
    const walk = (node: Node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const t = node.textContent ?? '';
            if (t.trim()) parts.push(t);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const e = node as Element;
        const tag = e.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'SVG' || tag === 'NOSCRIPT') return;
        if (e.hasAttribute('data-consent-ignore')) return;
        for (const child of Array.from(e.childNodes)) walk(child);
    };
    walk(el);
    return normalize(parts.join(' '));
}

async function sha256Hex(text: string): Promise<string | null> {
    try {
        if (!globalThis.crypto?.subtle) return null;
        const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
        return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
        return null;
    }
}

/** 실패해도 절대 throw 하지 않는다. 호출부는 `void recordTermsConsent(...)`로 결제 흐름과 분리해 부른다. */
export async function recordTermsConsent(opts: RecordTermsConsentOptions): Promise<void> {
    try {
        const text = normalize(redactText(opts.text ?? extractTermsText(opts.element), opts.redact));
        if (text.length < 20) {
            console.warn('[termsConsent] 약관 텍스트가 비어 기록을 건너뜀', opts.termsKey);
            return;
        }
        const clientHash = await sha256Hex(text);
        const meta: Record<string, unknown> = {
            ...(opts.meta ?? {}),
            ua: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 300) : undefined,
            lang: typeof navigator !== 'undefined' ? navigator.language : undefined,
            path: typeof location !== 'undefined' ? location.pathname : undefined,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            text_length: text.length,
            app_commit: APP_COMMIT || undefined,
            redacted: (opts.redact ?? []).filter((v) => (v ?? '').trim().length >= 2).length,
        };
        const params = {
            p_terms_key: opts.termsKey,
            p_terms_text: text.slice(0, 60000),
            p_reference_id: opts.referenceId ?? null,
            p_meta: meta,
            p_client_hash: clientHash,
        };
        // 네트워크 순단 대비 1회 재시도(2초 뒤). 2026-09-17 실측: 새 코드를 쓴 이용자도 결제 685건 중 2건은 기록이 안 남음.
        let lastErr: string | null = null;
        for (let attempt = 1; attempt <= 2; attempt += 1) {
            const { data, error } = await supabase.rpc('record_terms_consent', params);
            if (!error) {
                const res = (data ?? {}) as { success?: boolean; error?: string };
                if (!res.success) console.warn('[termsConsent] 서버 거절', opts.termsKey, res.error);
                return;
            }
            lastErr = error.message;
            if (attempt === 1) await new Promise((r) => setTimeout(r, 2000));
        }
        console.warn('[termsConsent] 기록 실패(재시도 후)', opts.termsKey, lastErr);
    } catch (e) {
        console.warn('[termsConsent] 기록 예외', opts.termsKey, e);
    }
}
